import { Plus, WebhooksLogo } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { spacesQuery } from "@/components/admin/api";
import { drawerSearch, QueryBoundary, useDrawerParam } from "@/components/admin/shared";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import { Badge, Button, type DataColumn, DataTable, EmptyState, RelativeTime, TableSkeleton } from "@/components/ui";
import { type WebhookView, webhooksQuery } from "@/components/webhooks/api";
import { DeliveriesDrawer, StatusBadge } from "@/components/webhooks/DeliveriesDrawer";
import { eventLabel, hostOf } from "@/components/webhooks/events";
import { SecretDialog, type SecretResult } from "@/components/webhooks/SecretDialog";
import { type Created, WebhookDialog } from "@/components/webhooks/WebhookDialog";

export const Route = createFileRoute("/_app/admin/webhooks")({
  validateSearch: drawerSearch("hook"),
  component: WebhooksPage,
});

function WebhooksPage() {
  useBreadcrumbs([{ label: m.adm_title(), to: "/admin" }, { label: m.adm_nav_webhooks() }]);
  const q = useQuery(webhooksQuery);
  const spaces = useQuery(spacesQuery).data;
  const [openId, setOpenId] = useDrawerParam("hook");
  const [editing, setEditing] = useState<{ hook: WebhookView | null } | null>(null);
  const [secret, setSecret] = useState<SecretResult | null>(null);

  const scopeLabel = (h: WebhookView) => {
    if (!h.productId) return m.wh_scope_instance();
    for (const s of spaces ?? []) {
      const p = s.products.find((x) => x.id === h.productId);
      if (p) return p.isSpaceProduct ? `${s.name}, ${m.space_wide()}` : p.name;
    }
    return m.wh_scope_product();
  };

  // After creating, the secret comes first; the new webhook's drawer opens once it is dismissed (two
  // overlays opened together would stack the drawer over the secret).
  const [openAfterSecret, setOpenAfterSecret] = useState<string | null>(null);
  const onCreated = (c: Created) => {
    setSecret({ name: c.webhook.name, url: c.webhook.url, secret: c.secret, rotated: false });
    setOpenAfterSecret(c.webhook.id);
  };

  return (
    <Page
      title={m.adm_nav_webhooks()}
      icon={WebhooksLogo}
      meta={m.wh_meta()}
      width="full"
      className="max-w-5xl"
      actions={
        <Button variant="primary" onPress={() => setEditing({ hook: null })}>
          <Plus size={14} aria-hidden="true" />
          {m.wh_create_title()}
        </Button>
      }
    >
      <QueryBoundary query={q} skeleton={<TableSkeleton rows={4} columns={4} />} needs="instance.admin">
        {(hooks) => (
          <HookTable
            hooks={hooks}
            scopeLabel={scopeLabel}
            onOpen={setOpenId}
            onCreate={() => setEditing({ hook: null })}
          />
        )}
      </QueryBoundary>

      <DeliveriesDrawer
        hook={q.data?.find((h) => h.id === openId) ?? null}
        scopeLabel={q.data?.find((h) => h.id === openId) ? scopeLabel(q.data.find((h) => h.id === openId)!) : ""}
        onClose={() => setOpenId(null)}
        onEdit={(hook) => setEditing({ hook })}
        onSecret={setSecret}
      />
      <WebhookDialog
        // A fresh form per target: the dialog reads its initial values once.
        key={editing?.hook?.id ?? "new"}
        isOpen={editing !== null}
        hook={editing?.hook ?? null}
        onOpenChange={(o) => !o && setEditing(null)}
        onCreated={onCreated}
      />
      <SecretDialog
        result={secret}
        onClose={() => {
          setSecret(null);
          if (openAfterSecret) setOpenId(openAfterSecret);
          setOpenAfterSecret(null);
        }}
      />
    </Page>
  );
}

function HookTable({
  hooks,
  scopeLabel,
  onOpen,
  onCreate,
}: {
  hooks: WebhookView[];
  scopeLabel: (h: WebhookView) => string;
  onOpen: (id: string) => void;
  onCreate: () => void;
}) {
  const columns: DataColumn<WebhookView>[] = [
    {
      id: "hook",
      header: m.wh_col_webhook(),
      isRowHeader: true,
      className: "w-full max-w-0 min-w-40",
      cell: (h) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate-title font-medium text-fg" title={h.name || h.url}>
            {h.name || hostOf(h.url)}
          </span>
          <span className="truncate-title font-mono text-xs text-fg-muted" title={h.url}>
            {h.url}
          </span>
        </div>
      ),
    },
    {
      id: "scope",
      header: m.wh_col_scope(),
      className: "hidden w-44 md:table-cell",
      cell: (h) => <span className="whitespace-nowrap text-fg-muted">{scopeLabel(h)}</span>,
    },
    {
      id: "events",
      header: m.wh_col_events(),
      className: "hidden w-36 lg:table-cell",
      cell: (h) => (
        <span className="whitespace-nowrap text-fg-muted" title={h.events.map(eventLabel).join(", ")}>
          {h.events.length === 0
            ? m.wh_events_all()
            : h.events.length === 1
              ? m.wh_events_count_one()
              : m.wh_events_count({ count: h.events.length })}
        </span>
      ),
    },
    {
      id: "status",
      header: m.wh_col_status(),
      className: "w-44",
      cell: (h) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusBadge hook={h} />
          {h.consecutiveFailures > 0 ? (
            <Badge tone="danger" numeric>
              {m.wh_failures({ count: h.consecutiveFailures })}
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      id: "last",
      header: m.wh_col_last(),
      className: "hidden w-36 whitespace-nowrap sm:table-cell",
      cell: (h) =>
        h.lastDeliveryAt ? (
          <RelativeTime value={h.lastDeliveryAt} className="text-fg-muted" />
        ) : (
          <span className="text-fg-subtle">{m.ui_never()}</span>
        ),
    },
  ];

  return (
    <DataTable
      label={m.adm_nav_webhooks()}
      columns={columns}
      rows={hooks}
      onRowAction={(h) => onOpen(h.id)}
      renderEmptyState={() => (
        <EmptyState
          icon={WebhooksLogo}
          title={m.wh_empty_title()}
          description={m.wh_empty_body()}
          action={
            <Button variant="secondary" onPress={onCreate}>
              {m.wh_create_title()}
            </Button>
          }
        />
      )}
    />
  );
}
